/**
 * shepherdRisksData — pure parsing for §6c Shepherd Risks.
 *
 * Shepherd security risks are top-level tasks in a single Asana project (the Security
 * Risks tracker), exported by the OE data pipeline into the shared `dim_asana_tasks`
 * dataset. Each risk's Severity / Status / ETA / detected-date live in the task's
 * `custom_fields` JSONB array (values are all serialised as strings; a field is omitted
 * when it has no value in Asana, so every lookup tolerates absence).
 */
import { logger } from 'src/logger';
import { SHEPHERD_PROJECT_GID } from '../../FinTechOpsConstants';

/** One entry of an Asana task's `custom_fields` JSONB array. */
export interface AsanaCustomField {
  name?: string;
  key?: string;
  value?: string;
  type?: string;
}

/** A raw Asana task row from the `dim_asana_tasks` S3 export. */
export interface ShepherdRiskRawTask {
  task_gid?: string;
  task_name?: string;
  primary_project_gid?: string | null;
  parent_task_gid?: string | null;
  completed?: boolean | string | null;
  permalink_url?: string | null;
  /** A JSON array, or a JSON-encoded string of that array (see `toCustomFields`). */
  custom_fields?: AsanaCustomField[] | string | null;
  [key: string]: unknown;
}

/** A parsed Shepherd risk row for the §6c table. */
export interface ShepherdRisk {
  taskGid: string;
  title: string;
  /** Priority custom field — "High" / "Critical" / "Medium"; empty when unset. */
  severity: string;
  /** Derived from Task Progress: "Done" → "Closed", otherwise "Assigned". */
  status: 'Closed' | 'Assigned';
  /** ECD custom field as an ISO string, or '' when unset. */
  eta: string;
  /** Issue Detected Date custom field as an ISO string, or '' when unset. */
  issueDetectedDate: string;
  /** Owner custom field (Asana people/text field); '' when no owner is set. */
  owner: string;
  permalinkUrl: string;
}

/** Custom-field keys the pipeline emits for Shepherd tasks (see BE change notes). */
const CF_KEY = {
  taskProgress: 'task_progress',
  priority: 'priority',
  ecd: 'ecd',
  issueDetectedDate: 'issue_detected_date',
  owner: 'owner'
} as const;

/**
 * Normalise a task's `custom_fields` into an array. The pipeline may emit it as a real
 * JSON array or as a JSON-encoded string (the latter is what the current export does, e.g.
 * `"[{\"key\":...}]"`); either is accepted. Anything unparseable yields an empty array.
 */
const toCustomFields = (raw: AsanaCustomField[] | string | null | undefined): AsanaCustomField[] => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as AsanaCustomField[]) : [];
    } catch {
      return [];
    }
  }
  return [];
};

/** Read a custom field's value by `key` (falls back to `name`), '' if absent. */
const cfValue = (fields: AsanaCustomField[], key: string, name?: string): string => {
  const match = fields.find((f) => f.key === key || (name !== undefined && f.name === name));
  const v = match?.value;
  return typeof v === 'string' ? v.trim() : '';
};

/**
 * A task is a Shepherd risk row: in the Shepherd project and top-level (no parent).
 * `!t.parent_task_gid` treats null, undefined, and '' as top-level.
 */
const isShepherdRiskRow = (t: ShepherdRiskRawTask): boolean => String(t.primary_project_gid ?? '') === SHEPHERD_PROJECT_GID && !t.parent_task_gid;

/**
 * Parse the raw Asana tasks export into the Shepherd risk rows for §6c.
 *
 * Keeps only top-level tasks in the Shepherd project (sibling sections / cross-project
 * subtasks share the dataset and are filtered out), mapping each task's custom fields to
 * severity / status / ETA / detected-date. Rows with no title are dropped.
 */
export const parseShepherdRisks = (raw: ShepherdRiskRawTask[] | null | undefined): ShepherdRisk[] => {
  if (!Array.isArray(raw)) return [];

  const matched = raw.filter(isShepherdRiskRow);
  // Surface a likely-misconfigured GID: the dataset had rows but none matched the Shepherd
  // project. Without this, §6c silently shows "No Shepherd risks" and the cause is opaque.
  if (raw.length > 0 && matched.length === 0) {
    logger.warn(`parseShepherdRisks: ${raw.length} Asana task rows but none matched SHEPHERD_PROJECT_GID=${SHEPHERD_PROJECT_GID} — check the GID.`);
  }

  return matched
    .map((t): ShepherdRisk => {
      const fields = toCustomFields(t.custom_fields);
      const progress = cfValue(fields, CF_KEY.taskProgress, 'Task Progress');
      // Task Progress is the authoritative status per the pipeline spec; the core
      // `completed` flag is a fallback so a done task without a "Done" progress value
      // isn't miscounted as open. `completed` is typed boolean | string | null, so match
      // both the boolean and string ("true") forms (String(true) === 'true').
      const isDone = progress.toLowerCase() === 'done' || String(t.completed).toLowerCase() === 'true';
      return {
        taskGid: String(t.task_gid ?? ''),
        title: (t.task_name ?? '').trim(),
        severity: cfValue(fields, CF_KEY.priority, 'Priority'),
        status: isDone ? 'Closed' : 'Assigned',
        eta: cfValue(fields, CF_KEY.ecd, 'ECD'),
        issueDetectedDate: cfValue(fields, CF_KEY.issueDetectedDate, 'Issue Detected Date'),
        // Owner is an Asana "Owner" custom field, NOT the core `assignee_name` (which is
        // null/unused on Shepherd tasks). Empty ('') when no owner is set in Asana.
        owner: cfValue(fields, CF_KEY.owner, 'Owner'),
        permalinkUrl: (t.permalink_url ?? '').trim()
      };
    })
    .filter((r) => r.title.length > 0);
};
